import React from "react";
import { Users, DollarSign, Target, TrendingUp } from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from "recharts";

const renderLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}) => {
  if (percent < 0.03) return null;

  const RADIAN = Math.PI / 180;

  const radius =
    innerRadius +
    (outerRadius - innerRadius) * 0.65;

  const x =
    cx + radius * Math.cos(-midAngle * RADIAN);

  const y =
    cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#f8fafc"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
    >
      {(percent * 100).toFixed(0)}%
    </text>
  );
};

const StatCard = ({ title, value, icon: Icon, iconBg, iconColor }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>

          <h2 className="mt-3 text-3xl font-bold text-gray-900">{value}</h2>
        </div>

        <div
          className={`h-12 w-12 rounded-xl flex items-center justify-center ${iconBg}`}
        >
          <Icon size={22} className={iconColor} />
        </div>
      </div>
    </div>
  );
};

const ClientStageOverview = ({ data }) => {
  if (!data) return null;
  // const wonCount =
  //   data.stageCounts?.filter((x) => x.stageType === "won").length || 0;

  const activeCount =
    data.stageCounts?.filter((x) => x.stageType === "active").length || 0;

  return (
    <div className="space-y-6">
      {/* CARDS */}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard
          title="Total Clients"
          value={data.totalClients}
          icon={Users}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />

        <StatCard
          title="Estimated Value"
          value={`₹${data.estimatedValue?.toLocaleString()}`}
          icon={DollarSign}
          iconBg="bg-green-50"
          iconColor="text-green-600"
        />

        <StatCard
          title="Expected Value"
          value={`₹${data.expectedValue?.toLocaleString()}`}
          icon={Target}
          iconBg="bg-purple-50"
          iconColor="text-purple-600"
        />

        <StatCard
          title="Active Stages"
          value={activeCount}
          icon={TrendingUp}
          iconBg="bg-orange-50"
          iconColor="text-orange-600"
        />
      </div>

      {/* CHARTS */}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* BAR */}

        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                Pipeline Stage Distribution
              </h3>

              <p className="text-sm text-gray-500 mt-1">
                Client count by sales stage
              </p>
            </div>

            <div className="px-3 py-1 rounded-full bg-red-50 text-red-600 text-xs font-semibold">
              {data.totalClients} Clients
            </div>
          </div>

          <div className="overflow-x-auto">
          <div className="min-w-[400px] h-[420px]">
            <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data.stageCounts}
              margin={{
                top: 20,
                right: 20,
                left: 0,
                bottom: 20,
              }}
            >
              <CartesianGrid
                strokeDasharray="4 4"
                vertical={false}
                stroke="#f1f5f9"
              />

              <XAxis
                dataKey="stageName"
                tick={{
                  fontSize: 12,
                  fill: "#64748b",
                }}
                axisLine={false}
                tickLine={false}
              />

              <YAxis
                allowDecimals={false}
                tick={{
                  fontSize: 12,
                  fill: "#64748b",
                }}
                axisLine={false}
                tickLine={false}
              />

              <Tooltip
                cursor={{
                  fill: "#f8fafc",
                }}
                contentStyle={{
                  borderRadius: "16px",
                  border: "none",
                  boxShadow:
                    "0 10px 30px rgba(0,0,0,0.08)",
                }}
              />

              <Bar
                dataKey="count"
                radius={[12, 12, 0, 0]}
                animationDuration={1200}
              >
                {data.stageCounts.map(
                  (entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.stageColor}
                    />
                  )
                )}
              </Bar>
            </BarChart>
              </ResponsiveContainer>
          </div>
        </div>
        </div>

        {/* DONUT */}

      <div className="relative overflow-hidden rounded-3xl border border-gray-100 bg-gradient-to-br from-white via-white to-blue-50 shadow-sm p-6">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-100/40 rounded-full blur-3xl" />

        <div className="relative flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              Pipeline Split
            </h3>

            <p className="text-sm text-gray-500 mt-1">
              Distribution across all stages
            </p>
          </div>

          <div className="px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
            {data.totalClients} Clients
          </div>
        </div>

        <ResponsiveContainer width="100%" height={420}>
          <PieChart>
            <Pie
              data={data.stageCounts}
              dataKey="count"
              nameKey="stageName"
              innerRadius={90}
              outerRadius={150}
              cornerRadius={10}
              paddingAngle={4}
              label={renderLabel}
              labelLine={false}
              animationDuration={1500}
            >
              {data.stageCounts.map((entry, index) => (
                <Cell
                  key={index}
                  fill={entry.stageColor}
                  stroke="#fff"
                  strokeWidth={2}
                  style={{
                    cursor: "pointer",
                  }}

                />
              ))}
            </Pie>

            <Tooltip
              formatter={(value, name, props) => {
                const percentage = (
                  (value / data.totalClients) *
                  100
                ).toFixed(1);

                return [
                  `${value} Clients (${percentage}%)`,
                  props.payload.stageName,
                ];
              }}
              contentStyle={{
                borderRadius: "18px",
                border: "none",
                background: "#fff",
                boxShadow:
                  "0 15px 40px rgba(0,0,0,0.12)",
              }}
            />



            <text
              x="50%"
              y="44%"
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-gray-500 text-sm"
            >
              Total Pipeline
            </text>

            <text
              x="50%"
              y="54%"
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-gray-900 font-bold text-4xl"
            >
              {data.totalClients}
            </text>
          </PieChart>
        </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-2 mt-4">
            {data.stageCounts.map((stage) => {
              const percentage =
                (
                  (stage.count /
                    data.totalClients) *
                  100
                ).toFixed(1);

              return (
                <div
                  key={stage._id}
                  className="flex items-center justify-between text-xs"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor:
                          stage.stageColor,
                      }}
                    />

                    <span>
                      {stage.stageName}
                    </span>
                  </div>

                  <span>
                    {percentage}%
                  </span>
                </div>
              );
            })}
          </div>
      </div>
      </div>

      {/* STAGE SUMMARY */}

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-xl font-bold text-gray-900">
            Stage Summary
          </h3>

          <p className="text-sm text-gray-500 mt-1">
            Performance breakdown by pipeline stage
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Stage
                </th>

                <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Clients
                </th>

                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Estimated
                </th>

                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Expected
                </th>

                <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Probability
                </th>
              </tr>
            </thead>

            <tbody>
              {data.stageCounts.map((stage) => (
                <tr
                  key={stage._id}
                  className="
                    border-t
                    border-gray-100
                    hover:bg-gray-50
                    transition-all
                  "
                >
                  {/* Stage */}

                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{
                          backgroundColor:
                            stage.stageColor,
                        }}
                      />

                      <div>
                        <p className="font-semibold text-gray-900">
                          {stage.stageName}
                        </p>

                        <p className="text-xs text-gray-500">
                          Sales Stage
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Clients */}

                  <td className="px-6 py-4 text-center">
                    <span
                      className="
                        px-3
                        py-1
                        rounded-full
                        bg-blue-50
                        text-blue-700
                        text-sm
                        font-semibold
                      "
                    >
                      {stage.count}
                    </span>
                  </td>

                  {/* Estimated */}

                  <td className="px-6 py-4 text-right">
                    <p className="font-semibold text-gray-900">
                      ₹
                      {stage.estimatedValue?.toLocaleString()}
                    </p>
                  </td>

                  {/* Expected */}

                  <td className="px-6 py-4 text-right">
                    <p className="font-semibold text-green-600">
                      ₹
                      {stage.expectedValue?.toLocaleString()}
                    </p>
                  </td>

                  {/* Probability */}

                  <td className="px-6 py-4 text-center">

                        {stage.probability}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ClientStageOverview; 